import React, { useState } from 'react';
import { Paper, Group, Text, Button, NumberInput, Notification, MantineProvider, Slider, Loader, SegmentedControl } from '@mantine/core';
import { IconDownload, IconCheck } from '@tabler/icons-react';
import { deepPresetsClient } from '../../api/deepPresetsClient';

const theme = {
  background: {
    primary: '#0A0D14',
    secondary: '#161B22',
    tertiary: '#21262D',
    elevated: '#2D333B'
  },
  accent: {
    primary: '#4F46E5',
    secondary: '#7C3AED',
    tertiary: '#059669'
  },
  state: {
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B'
  },
  text: {
    primary: '#F9FAFB',
    secondary: '#9CA3AF',
    disabled: '#6B7280'
  },
  border: {
    primary: '#374151',
    secondary: '#4B5563'
  }
};

const modernStyles = {
  container: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${theme.background.primary} 0%, ${theme.background.secondary} 100%)`,
    color: theme.text.primary,
    padding: '12px',
    fontFamily: 'Space Grotesk, sans-serif'
  },
  card: {
    backgroundColor: theme.background.secondary,
    borderRadius: '12px',
    border: `1px solid ${theme.border.primary}`,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(12px)'
  }
};

function expSliderValue(val: number) {
  // Map 0-100 to 1h-240h (10 days) exponentially
  // 1h * (240/1)^(val/100)
  const min = 1;
  const max = 240;
  const exp = val / 100;
  return Math.round(min * Math.pow(max / min, exp));
}
function expSliderPosition(hours: number) {
  // Inverse of expSliderValue
  const min = 1;
  const max = 240;
  return Math.round(100 * Math.log(hours / min) / Math.log(max / min));
}

export const ExportTokensMcapPage: React.FC = () => {
  const [sliderVal, setSliderVal] = useState(0); // 0..100
  const [hours, setHours] = useState(1);
  const [fromMcap, setFromMcap] = useState<number | ''>(3000);
  const [tillMcap, setTillMcap] = useState<number | ''>(35000);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [sliderChanging, setSliderChanging] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<'migrated' | 'not_migrated' | 'any'>('not_migrated');
  const [platform, setPlatform] = useState<'PumpFun' | 'Raydium'>('PumpFun');

  // Sync slider and hours
  const handleSliderChange = (val: number) => {
    setSliderVal(val);
    setHours(expSliderValue(val));
  };
  const handleSliderChangeStart = () => setSliderChanging(true);
  const handleSliderChangeEnd = () => setSliderChanging(false);

  const handleHoursInput = (val: string | number) => {
    if (val === '' || val === null) {
      setHours(1);
      setSliderVal(expSliderPosition(1));
      return;
    }
    const num = typeof val === 'string' ? Number(val) : val;
    if (!isNaN(num) && num >= 1 && num <= 240) {
      setHours(num);
      setSliderVal(expSliderPosition(num));
    }
  };

  const handleFromMcap = (val: string | number) => {
    if (val === '' || val === null) {
      setFromMcap('');
    } else {
      const num = typeof val === 'string' ? Number(val) : val;
      setFromMcap(isNaN(num) ? '' : num);
    }
  };
  const handleTillMcap = (val: string | number) => {
    if (val === '' || val === null) {
      setTillMcap('');
    } else {
      const num = typeof val === 'string' ? Number(val) : val;
      setTillMcap(isNaN(num) ? '' : num);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await deepPresetsClient.exportTokensMcap({
        hours,
        from_dollar_mcap: fromMcap === '' ? undefined : fromMcap,
        till_dollar_mcap: tillMcap === '' ? undefined : tillMcap,
        migrate_status: migrationStatus,
        platform,
      });
      setResult(res?.message || 'Export done');
    } catch (e: any) {
      setResult(e?.message || 'Export failed');
    } finally {
      setLoading(false);
    }
    await new Promise(r => setTimeout(r, 2000));
    console.log('Closing WebApp after export');
    Telegram.WebApp.close();
  };

  return (
    <MantineProvider theme={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      <div style={modernStyles.container}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <Text
            size="24px"
            fw={700}
            mb={20}
            style={{
              background: `linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.secondary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center'
            }}
          >
            Export Tokens by Hit MCAP
          </Text>
          {result && (
              <Notification
                color={result.toLowerCase().includes('fail') ? "red" : "green"}
                withCloseButton
                onClose={() => setResult(null)}
                style={{ marginTop: 16 }}
                icon={<IconCheck />}
              >
                {result}
              </Notification>
            )}
          <Paper p="md" mb="md" style={modernStyles.card}>
            {/* --- Platform SegmentedControl --- */}
            <div style={{ marginBottom: 18 }}>
              <Text size="sm" fw={500} mb={6} style={{ color: theme.text.secondary }}>
                Platform
              </Text>
              <SegmentedControl
                value={platform}
                onChange={val => setPlatform(val as 'PumpFun' | 'Raydium')}
                data={[
                  { label: '💊PumpFun', value: 'PumpFun' },
                  { label: '🚀Raydium', value: 'Raydium' },
                ]}
                fullWidth
                color={theme.accent.primary}
                styles={{
                  root: {
                    background: theme.background.tertiary,
                    border: `1px solid ${theme.border.primary}`,
                    borderRadius: 8,
                    padding: 2,
                  },
                  indicator: {
                    background: 'rgba(79,70,229,0.13)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: 6,
                    boxShadow: `0 2px 8px 0 ${theme.accent.primary}40`,
                    border: `1.5px solid ${theme.accent.primary}`,
                  },
                  label: {
                    color: theme.text.primary,
                    fontWeight: 500,
                    fontSize: 14,
                    padding: '6px 0',
                  },
                  control: {
                    borderRadius: 6,
                  },
                }}
              />
            </div>
            <Text fw={500} size="sm" mb={8}>Max time ago:</Text>
            <Group align="center" mb={8}>
              <Text size="sm" fw={500} c={theme.text.secondary}>
                {hours < 24
                  ? `${hours} hour${hours === 1 ? '' : 's'}`
                  : `${Math.floor(hours / 24)} day${Math.floor(hours / 24) === 1 ? '' : 's'}${hours % 24 !== 0 ? ` ${hours % 24}h` : ''}`
                }
              </Text>
            </Group>
            <Group align="center" mb={28}>
              <Slider
                min={0}
                max={100}
                step={12.5}
                value={sliderVal}
                onChange={(val) => {
                  if (!sliderChanging) setSliderChanging(true);
                  handleSliderChange(val);
                }}
                onPointerDown={() => setSliderChanging(true)}
                onPointerUp={() => setSliderChanging(false)}
                style={{ flex: 1, marginTop: 8, marginBottom: 8 }}
                marks={[
                  { value: 0, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>1h</span> },
                  { value: 12.5, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>2h</span> },
                  { value: 25, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>4h</span> },
                  { value: 37.5, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>8h</span> },
                  { value: 50, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>1d3h</span> },
                  { value: 62.5, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>1d7h</span> },
                  { value: 75, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>2d13h</span> },
                  { value: 87.5, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>5d1h</span> },
                  { value: 100, label: <span style={{ color: theme.text.secondary, fontWeight: 500 }}>10d</span> }
                ]}
                color={theme.accent.primary}
                thumbSize={16}
                styles={{
                  track: {
                    background: `linear-gradient(90deg, ${theme.background.tertiary} 0%, ${theme.background.elevated} 100%)`,
                    height: 8,
                    borderRadius: 8,
                    border: `1px solid ${theme.border.primary}`,
                  },
                  bar: {
                    background: `linear-gradient(90deg, ${theme.accent.primary} 0%, ${theme.accent.secondary} 100%)`,
                    height: 8,
                    borderRadius: 8,
                  },
                  thumb: {
                    border: `2px solid ${theme.accent.primary}`,
                    background: theme.background.secondary,
                    boxShadow: '0 2px 8px 0 rgba(79,70,229,0.15)',
                    width: 18,
                    height: 18,
                  },
                  mark: {
                    display: 'none',
                  },
                  markLabel: {
                    color: theme.text.secondary,
                    fontWeight: 500,
                    fontSize: 13,
                    marginTop: 6,
                  },
                }}
              />
            </Group>
            {/* SegmentedControl for Migration status */}
            <div style={{ marginBottom: 18 }}>
              <Text size="sm" fw={500} mb={6} style={{ color: theme.text.secondary }}>
                Migration status (PF: SWAPS, Raydium: CPMM)
              </Text>
              <SegmentedControl
                value={migrationStatus}
                onChange={val => setMigrationStatus(val as 'migrated' | 'not_migrated' | 'any')}
                data={[
                  { label: 'Migrated', value: 'migrated' },
                  { label: 'Not migrated', value: 'not_migrated' },
                  { label: 'Any', value: 'any' },
                ]}
                fullWidth
                color={theme.state.success}
                styles={{
                  root: {
                    background: theme.background.tertiary,
                    border: `1px solid ${theme.border.primary}`,
                    borderRadius: 8,
                    padding: 2,
                  },
                  indicator: {
                    // Glass green with shadow, like theme.state.success15
                    background: 'rgba(16,185,129,0.13)', // theme.state.success15
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: 6,
                    boxShadow: `0 2px 8px 0 ${theme.state.success}40`,
                    border: `1.5px solid ${theme.state.success}`,
                  },
                  label: {
                    color: theme.text.primary,
                    fontWeight: 500,
                    fontSize: 14,
                    padding: '6px 0',
                  },
                  control: {
                    borderRadius: 6,
                  },
                }}
              />
            </div>
            <div style={{ height: 8 }} />
            <Group grow mb={16}>
              <NumberInput
                label="Min MCAP ($)"
                min={3000}
                max={typeof tillMcap === 'number' ? tillMcap : undefined}
                value={fromMcap}
                onChange={handleFromMcap}
                size="sm"
                inputMode="numeric"
                pattern="[0-9]*"
                styles={{
                  input: {
                    backgroundColor: theme.background.tertiary,
                    border: `1px solid ${theme.border.primary}`,
                    color: theme.text.primary,
                  }
                }}
                hideControls
              />
              <NumberInput
                label="Max MCAP ($)"
                min={typeof fromMcap === 'number' ? fromMcap : 0}
                max={1000000}
                value={tillMcap}
                onChange={handleTillMcap}
                size="sm"
                inputMode="numeric"
                pattern="[0-9]*"
                styles={{
                  input: {
                    backgroundColor: theme.background.tertiary,
                    border: `1px solid ${theme.border.primary}`,
                    color: theme.text.primary,
                  }
                }}
                hideControls
              />
            </Group>
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                background: theme.background.secondary,
                paddingTop: 16,
                marginLeft: -16,
                marginRight: -16,
                marginBottom: -16,
                paddingLeft: 16,
                paddingRight: 16,
                boxShadow: '0 -4px 24px 0 rgba(0,0,0,0.12)',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
              }}
            >
              <Group justify="center">
                <Button
                  leftSection={<IconDownload size={16} />}
                  onClick={handleExport}
                  size="md"
                  fullWidth
                  loading={loading}
                  styles={{
                    root: {
                      background: `linear-gradient(135deg, ${theme.accent.tertiary} 0%, #059669 100%)`,
                      border: 'none',
                      color: theme.text.primary,
                      fontWeight: 500,
                      minHeight: '40px',
                      marginBottom: '20px',
                      fontSize: '16px',
                      '&:hover': {
                        background: `linear-gradient(135deg, #047857 0%, #065F46 100%)`,
                        transform: 'translateY(-1px)'
                      }
                    }
                  }}
                >
                  Export tokens
                </Button>
              </Group>
            </div>
          </Paper>
        </div>
      </div>
    </MantineProvider>
  );
};

export default ExportTokensMcapPage;


